import { useCallback, useEffect, useState } from 'react';
import { hojeBrasilia, lerCupom, lerMovimentacao } from '../../central';
import type { DetalheDoCupom, LinhaDaMovimentacao, Movimentacao as Dados } from '../../central';
import { ErroDeArea } from '../../design/Estados';
import { Icone } from '../../design/Icone';
import { fmt } from '../../design/graficos/modelos';
import { reais } from '../../produtos';
import { hora } from '../../vendas';
import { BarraDeFiltros, Kpi, irPara, lerFiltros, textoDoPeriodo } from '../paineis/pecasDaVisao';
import { SubnavDeVendas } from './SubnavDeVendas';
import './central.css';
import { linhaClicavel } from '../../design/linha-clicavel';

/**
 * TELA 13 — MOVIMENTAÇÃO e DETALHE DO CUPOM. Vendas, canceladas e devoluções do HOS numa lista só,
 * paginada (100 por página), com filtros no endereço: período, tipo, balconista e busca (cupom,
 * produto, código ou EAN). Os totais de cima são do período inteiro, não da página. A devolução
 * vem da API do HOS, que não traz o motivo — "motivo não informado", nunca um motivo inventado.
 * O detalhe do cupom é página com rota própria (#/cupom?id=), não modal.
 */

const SIGLA: Record<LinhaDaMovimentacao['tipo'], string> = {
  venda: 'VENDA',
  devolucao: 'DEVOL.',
  cancelamento: 'CANCEL.',
};
const STATUS: Record<LinhaDaMovimentacao['status'], string> = {
  concluida: 'Concluída',
  cancelada: 'Cancelada',
  motivo_nao_informado: 'Motivo não informado',
  devolvida: 'Devolvida',
};
type Tipo = '' | 'venda' | 'cancelamento' | 'devolucao';
const TIPOS: readonly [Tipo, string][] = [
  ['', 'Tudo'],
  ['venda', 'Vendas'],
  ['cancelamento', 'Canceladas'],
  ['devolucao', 'Devoluções'],
];

const negativo = (l: LinhaDaMovimentacao) => l.tipo !== 'venda';
const valorDaLinha = (l: LinhaDaMovimentacao) =>
  l.valor === '' ? '—' : negativo(l) ? `−${reais(l.valor.replace('-', ''))}` : reais(l.valor);
const diaCurto = (d: string) => d.split('-').reverse().slice(0, 2).join('/');

export function TabelaDeMovimentacao({
  linhas,
  vazio,
  comDia = false,
}: {
  linhas: readonly LinhaDaMovimentacao[];
  vazio: string;
  /** Período de vários dias: a coluna mostra o dia junto da hora. */
  comDia?: boolean;
}) {
  if (linhas.length === 0) return <p className="cv-vazio">{vazio}</p>;
  return (
    <div className="cv-rolagem">
      <table className="cv-tabela">
        <thead>
          <tr>
            <th>{comDia ? 'Quando' : 'Hora'}</th>
            <th>Cupom</th>
            <th>Tipo</th>
            <th>Balconista</th>
            <th>Resumo</th>
            <th className="cv-dir">Itens</th>
            <th className="cv-dir">Valor</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => {
            const abre = l.saleId;
            return (
              <tr
                key={l.id}
                className={abre ? 'cv-clicavel' : ''}
                {...(abre
                  ? linhaClicavel(() => (window.location.hash = `#/cupom?id=${abre}`))
                  : {})}
              >
                <td className="cv-mono cv-cinza">
                  {l.em
                    ? comDia
                      ? `${diaCurto(l.dia)} ${hora(l.em)}`
                      : hora(l.em)
                    : diaCurto(l.dia)}
                </td>
                <td className="cv-mono">{l.cupom}</td>
                <td>
                  <span className={`cv-tipo cv-tipo-${l.tipo}`}>{SIGLA[l.tipo]}</span>
                </td>
                <td>{l.balconista ?? <span className="cv-cinza">não informado</span>}</td>
                <td className="cv-forte">{l.resumo}</td>
                <td className="cv-dir">{l.itens}</td>
                <td className={`cv-dir cv-forte ${negativo(l) ? 'cv-neg' : ''}`}>
                  {valorDaLinha(l)}
                </td>
                <td>
                  <span className={`cv-status cv-status-${l.status}`}>
                    {l.status === 'concluida' && <Icone nome="check" tamanho={12} traco={2} />}
                    {STATUS[l.status]}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export function MovimentacaoDeVendas({ parametros }: { parametros: URLSearchParams }) {
  const hoje = hojeBrasilia();
  const f = lerFiltros(parametros, hoje);
  const { de, ate } = f.periodo;
  const tipo = (
    TIPOS.some(([t]) => t === parametros.get('tipo')) ? parametros.get('tipo') : ''
  ) as Tipo;
  const vendedor = /^\d{1,10}$/.test(parametros.get('vendedor') ?? '')
    ? parametros.get('vendedor')
    : null;
  const busca = (parametros.get('busca') ?? '').slice(0, 80);
  const pagina = Math.max(1, Number(parametros.get('pagina')) || 1);
  const extras = { tipo: tipo || null, vendedor, busca: busca || null };
  const [texto, setTexto] = useState(busca);
  const [d, setD] = useState<Dados | null>(null);
  const [erro, setErro] = useState(false);
  useEffect(() => setTexto(busca), [busca]);

  const ir = (mudar: Partial<typeof extras> & { pagina?: number }) => {
    const { pagina: pg, ...resto } = mudar;
    irPara({ filtro: f.filtro, periodo: f.periodo, pessoa: null }, 'movimentacao', {
      ...extras,
      ...resto,
      pagina: pg && pg > 1 ? String(pg) : null,
    });
  };

  const carregar = useCallback(() => {
    setErro(false);
    setD(null);
    void lerMovimentacao(de, ate, {
      tipo: tipo || null,
      busca: busca || null,
      vendedor,
      pagina,
    }).then((r) => (r.ok ? setD(r.dados) : setErro(true)));
  }, [de, ate, tipo, busca, vendedor, pagina]);
  useEffect(carregar, [carregar]);

  const quando = textoDoPeriodo(f.filtro, f.periodo, hoje);
  const r = d?.resumo;
  const ultimaPagina = d ? Math.max(1, Math.ceil(d.total / d.porPagina)) : 1;
  const nomeDoVendedor = d?.balconistas.find((b) => b.codigo === vendedor)?.nome;

  return (
    <section className="cv">
      <SubnavDeVendas atual="movimentacao" />
      <div className="cv-corpo vg">
        <header className="vg-topo">
          <div className="vg-titulo">
            <h1>Movimentação</h1>
            <div className="vg-contexto">
              Vendas, canceladas e devoluções do HOS · {quando}
              {nomeDoVendedor ? ` · ${nomeDoVendedor}` : ''}
            </div>
          </div>
        </header>

        <BarraDeFiltros f={f} hoje={hoje} rota="movimentacao" extras={extras} />

        <div className="vg-kpis cv-kpis-5">
          <Kpi
            rotulo={`Cupons · ${quando}`}
            icone="receipt"
            valor={r ? fmt(r.cupons, 'inteiro') : null}
            variacao={null}
            sub={r ? `${fmt(r.itens, 'inteiro')} itens vendidos` : null}
            link="#/movimentacao"
            destaque
          />
          <Kpi
            rotulo="Faturamento"
            icone="money"
            valor={r ? fmt(Number(r.faturamento), 'reais') : null}
            variacao={null}
            sub={r && r.cupons ? `ticket ${fmt(Number(r.faturamento) / r.cupons, 'reais')}` : null}
            link="#/vendas"
          />
          <Kpi
            rotulo="Itens por cupom"
            icone="pill"
            valor={r ? (r.cupons ? fmt(r.itens / r.cupons, 'decimal') : '—') : null}
            variacao={null}
            sub={r ? 'quantos itens cada cliente leva' : null}
            link="#/vendas"
          />
          <Kpi
            rotulo="Cancelado no caixa"
            icone="x"
            valor={r ? `${fmt(r.canceladas.itens, 'inteiro')} itens` : null}
            variacao={null}
            sub={
              r
                ? `${fmt(r.canceladas.cupons, 'inteiro')} cupom(ns) inteiro(s)${Number(r.canceladas.valor) ? ` · ${fmt(Number(r.canceladas.valor), 'reais')}` : ''}`
                : null
            }
            link="#/movimentacao?tipo=cancelamento"
          />
          <Kpi
            rotulo="Devoluções"
            icone="arrow-l"
            valor={r ? fmt(r.devolucoes.cupons, 'inteiro') : null}
            variacao={null}
            sub={r ? `${fmt(Number(r.devolucoes.valor), 'reais')} devolvidos` : null}
            link="#/devolucoes"
          />
        </div>

        <div className="vg-filtros cv-filtros-lista" role="toolbar" aria-label="Filtros da lista">
          <div className="vg-segmentos" role="group" aria-label="Tipo">
            {TIPOS.map(([t, rot]) => (
              <button
                key={t || 'tudo'}
                type="button"
                className={`vg-seg${tipo === t ? ' vg-seg-ativo' : ''}`}
                aria-pressed={tipo === t}
                onClick={() => ir({ tipo: t || null })}
              >
                {rot}
                {r && (
                  <em className="cv-seg-num">
                    {t === 'venda'
                      ? fmt(r.cupons, 'inteiro')
                      : t === 'cancelamento'
                        ? fmt(r.canceladas.itens + r.canceladas.cupons, 'inteiro')
                        : t === 'devolucao'
                          ? fmt(r.devolucoes.cupons, 'inteiro')
                          : ''}
                  </em>
                )}
              </button>
            ))}
          </div>
          <label className="vg-pessoa">
            <Icone nome="users" tamanho={14} traco={1.6} />
            <select
              value={vendedor ?? ''}
              aria-label="Balconista"
              onChange={(e) => ir({ vendedor: e.target.value || null })}
            >
              <option value="">Todos os balconistas</option>
              {(d?.balconistas ?? []).map((b) => (
                <option key={b.codigo} value={b.codigo}>
                  {b.nome} · {b.codigo} ({fmt(b.cupons, 'inteiro')})
                </option>
              ))}
            </select>
          </label>
          <form
            className="cv-busca-nova"
            onSubmit={(e) => {
              e.preventDefault();
              ir({ busca: texto.trim() || null });
            }}
          >
            <Icone nome="search" tamanho={15} traco={1.7} />
            <input
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              placeholder="Cupom, produto, código ou EAN"
              aria-label="Buscar cupom, produto, código ou EAN"
            />
            {busca && (
              <button type="button" aria-label="Limpar busca" onClick={() => ir({ busca: null })}>
                <Icone nome="x" tamanho={13} traco={1.8} />
              </button>
            )}
          </form>
        </div>

        <div className="vg-bloco cv-lista">
          {erro ? (
            <ErroDeArea oQue="a movimentação" aoTentar={carregar} />
          ) : d ? (
            <>
              <TabelaDeMovimentacao
                linhas={d.linhas}
                comDia={de !== ate}
                vazio={
                  d.fonteDisponivel
                    ? 'Nada encontrado com estes filtros.'
                    : 'Nenhuma venda do HOS no período.'
                }
              />
              <div className="cv-paginacao">
                <span>
                  {d.total
                    ? `${fmt((d.pagina - 1) * d.porPagina + 1, 'inteiro')}–${fmt(Math.min(d.total, d.pagina * d.porPagina), 'inteiro')} de ${fmt(d.total, 'inteiro')}`
                    : '0 linhas'}
                </span>
                <span className="cv-paginacao-botoes">
                  <button
                    type="button"
                    className="vg-botao"
                    disabled={d.pagina <= 1}
                    onClick={() => ir({ pagina: d.pagina - 1 })}
                  >
                    <Icone nome="arrow-l" tamanho={14} traco={1.8} />
                    Anterior
                  </button>
                  <span>
                    página {d.pagina} de {ultimaPagina}
                  </span>
                  <button
                    type="button"
                    className="vg-botao"
                    disabled={d.pagina >= ultimaPagina}
                    onClick={() => ir({ pagina: d.pagina + 1 })}
                  >
                    Próxima
                    <Icone nome="chev-r" tamanho={14} traco={1.8} />
                  </button>
                </span>
              </div>
            </>
          ) : (
            <div className="vg-lista-osso" aria-busy="true">
              {[0, 1, 2, 3, 4, 5, 6].map((k) => (
                <i key={k} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

/** `destaque`: o produto que levou até este cupom (drill-down da central de descontos) fica marcado. */
export function CupomDoHos({ id, destaque = null }: { id: string; destaque?: string | null }) {
  const [d, setD] = useState<DetalheDoCupom | null>(null);
  const [erro, setErro] = useState<'nao' | 'falha' | null>(null);
  const carregar = useCallback(() => {
    setErro(null);
    void lerCupom(id).then((r) => {
      if (r.ok) setD(r.dados);
      else setErro(r.falha.tipo === 'inesperado' ? 'nao' : 'falha');
    });
  }, [id]);
  useEffect(carregar, [carregar]);

  const quando = d
    ? new Date(d.em)
        .toLocaleString('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          timeZone: 'America/Sao_Paulo',
        })
        .replace(',', ' às')
    : '';
  const temDesconto = d?.itens.some((i) => i.descontoPct !== null) ?? false;
  const semCusto = d ? d.itens.filter((i) => i.custo === null).length : 0;
  return (
    <section className="cv">
      <SubnavDeVendas atual="movimentacao" />
      <div className="cv-corpo cv-cupom">
        <a href="#/movimentacao" className="cv-voltar">
          <Icone nome="arrow-l" tamanho={14} traco={1.8} /> Movimentação
        </a>
        {erro === 'nao' && <p className="cv-vazio">Cupom não encontrado.</p>}
        {erro === 'falha' && <ErroDeArea oQue="o cupom" aoTentar={carregar} />}
        {d && (
          <div className="cv-cartao cv-sem-pad">
            <div className="cv-cupom-topo">
              <h1>
                Cupom {d.cupom}
                <span className={`cv-status cv-status-${d.cancelada ? 'cancelada' : 'concluida'}`}>
                  {!d.cancelada && <Icone nome="check" tamanho={12} traco={2} />}
                  {d.cancelada ? 'Cancelada' : 'Concluída'}
                </span>
              </h1>
              <p>
                {quando}
                {d.balconista ? ` · ${d.balconista}` : ' · balconista não informado'}
              </p>
            </div>
            <div className="cv-cupom-faixa">
              <div>
                <span>TOTAL</span>
                <strong>{reais(d.total)}</strong>
              </div>
              <div>
                <span>ITENS</span>
                <strong>{d.itens.reduce((s, i) => s + i.quantidade, 0)}</strong>
              </div>
              <div>
                <span title="Ponderado pelo dinheiro: Σ desconto R$ ÷ Σ preço de tabela">
                  DESCONTO
                </span>
                <strong>
                  {temDesconto && d.descontoPonderado !== null
                    ? `${fmt(d.descontoPonderado, 'pct')} · ${reais(d.descontoRs)}`
                    : '—'}
                </strong>
              </div>
              <div>
                <span>
                  MARGEM {d.custoRestrito && <Icone nome="lock" tamanho={11} traco={1.6} />}
                </span>
                <strong className={d.margem === null ? 'cv-cinza' : d.margem < 0 ? 'cv-neg' : ''}>
                  {d.margem === null ? '—' : fmt(d.margem, 'pct')}
                </strong>
              </div>
            </div>
            <div className="cv-pad">
              <div className="cv-cartao-topo">
                <h2>Itens do cupom</h2>
                {d.custoRestrito && (
                  <span className="cv-cinza cv-peq">
                    <Icone nome="lock" tamanho={11} traco={1.6} /> custo e margem visíveis só para
                    quem configura produtos
                  </span>
                )}
              </div>
            </div>
            <div className="cv-rolagem">
              <table className="cv-tabela">
                <thead>
                  <tr>
                    <th>Produto</th>
                    <th className="cv-dir">Qtd</th>
                    <th className="cv-dir">PMC</th>
                    <th
                      className="cv-dir"
                      title="Preço de tabela do HOS, derivado do desconto: praticado ÷ (1 − desconto%)"
                    >
                      Original
                    </th>
                    <th className="cv-dir">Praticado</th>
                    <th className="cv-dir">Desconto</th>
                    <th className="cv-dir">Desconto R$</th>
                    <th className="cv-dir">Custo</th>
                    <th className="cv-dir">Margem</th>
                  </tr>
                </thead>
                <tbody>
                  {d.itens.map((i, k) => (
                    <tr
                      key={k}
                      className={destaque && i.productId === destaque ? 'dc-destaque' : undefined}
                    >
                      <td>
                        <div className="cv-forte">
                          {i.produto}
                          {destaque && i.productId === destaque && (
                            <span className="dc-investigado">produto investigado</span>
                          )}
                        </div>
                        <div className="cv-mono cv-cinza cv-peq">
                          {[i.codigo, i.ean].filter(Boolean).join(' · ') || '—'}
                        </div>
                      </td>
                      <td className="cv-dir">{i.quantidade}</td>
                      <td className="cv-dir cv-cinza">{reais(i.pmc)}</td>
                      <td className="cv-dir cv-cinza">{reais(i.precoOriginal)}</td>
                      <td className="cv-dir cv-forte">{reais(i.praticado)}</td>
                      <td className="cv-dir">
                        {i.descontoPct === null ? '—' : fmt(i.descontoPct, 'pct')}
                      </td>
                      <td className="cv-dir">{reais(i.descontoRs)}</td>
                      <td
                        className="cv-dir cv-cinza"
                        title={
                          i.fonteDoCusto === 'venda'
                            ? 'custo do dia da venda (API do HOS)'
                            : i.fonteDoCusto === 'cadastro'
                              ? 'custo do cadastro atual (a venda não trouxe custo)'
                              : undefined
                        }
                      >
                        {reais(i.custo)}
                        {i.fonteDoCusto === 'cadastro' && <sup> *</sup>}
                      </td>
                      <td
                        className={`cv-dir cv-forte ${i.margem !== null && i.margem < 0 ? 'cv-neg' : ''}`}
                      >
                        {i.margem === null ? '—' : fmt(i.margem, 'pct')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="cv-pad">
              {d.lancamentos.length > 0 && (
                <div className="cv-nota">
                  <strong>Premiação gerada:</strong>{' '}
                  {d.lancamentos
                    .map(
                      (l) =>
                        `${l.produto} (${String(l.quantidade)}×) ${reais(l.premio)} para ${l.balconista}`,
                    )
                    .join(' · ')}
                  .{' '}
                  <span className="cv-cinza">
                    Valor calculado pela regra do InovaPay, não pela Central.
                  </span>
                </div>
              )}
              {!d.custoRestrito && semCusto > 0 && (
                <div className="cv-aviso">
                  <Icone nome="warn" tamanho={14} traco={1.7} />
                  {semCusto === 1 ? '1 item sem custo' : `${String(semCusto)} itens sem custo`} na
                  fonte (o HOS mandou custo vazio ou de R$ 0,01): margem calculada só sobre os itens
                  com custo. * = custo do cadastro atual, não do dia da venda.
                </div>
              )}
              <h2 className="cv-h2">Linha do tempo</h2>
              <ul className="cv-linha-do-tempo">
                {d.linhaDoTempo.map((e, k) => (
                  <li key={k}>
                    <i />
                    <strong>{hora(e.em)}</strong> · {e.texto}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
